import {
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@outschool/backpack";
import { faComment, faPlus, faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import React from "react";

import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadListItem from "./ThreadListItem";
import { ConversationListItem } from "./types";
import {
  getConversationKey,
  isGeneralThread,
  sortConversationsByLatestSentAt,
  toConversationListItem,
} from "./utils";

interface ThreadListBodyProps {
  items: ConversationListItem[];
  threadsLoading: boolean;
  feedbackThreadsLoading: boolean;
  showEmptyConversationsState: boolean;
  teacherName: string;
  debouncedSearch: string;
  onConversationClick: (item: ConversationListItem) => void;
  onOpenGeneralThread: () => void;
}

function ThreadListBody({
  items,
  threadsLoading,
  feedbackThreadsLoading,
  showEmptyConversationsState,
  teacherName,
  debouncedSearch,
  onConversationClick,
  onOpenGeneralThread,
}: ThreadListBodyProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadListView",
  );

  if ((threadsLoading || feedbackThreadsLoading) && items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (showEmptyConversationsState) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 24,
          gap: 12,
        }}
      >
        <Icon
          icon={faComment}
          sx={{ color: "neutral.300", fontSize: "4rem" }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("No conversations yet")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", maxWidth: 300 }}
        >
          {t(
            'Explore a lesson and use the "Ask a question" button below your lesson to start chatting with your teacher.',
          )}
        </Typography>
        <Button
          variant="outlined"
          onClick={onOpenGeneralThread}
          sx={{
            mt: 8,
            borderColor: "neutral.200",
            color: "text.primary",
            borderRadius: "1.4rem",
            textTransform: "none",
          }}
        >
          {t("Message {{teacherName}} directly", {
            teacherName,
          })}
        </Button>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("No messages found")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {items.map(item => (
        <ThreadListItem
          key={getConversationKey(item)}
          item={item}
          teacherName={teacherName}
          onClick={() => onConversationClick(item)}
        />
      ))}
      {!debouncedSearch &&
        !items.some(
          item => item.kind === "thread" && isGeneralThread(item.thread),
        ) && (
          <Box
            data-test-id="ask-anything-row"
            onClick={onOpenGeneralThread}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              py: 12,
              px: 16,
              borderBottom: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "primary.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon
                icon={faPlus}
                sx={{ color: "primary.main", fontSize: "1.2rem" }}
              />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              {t("Ask {{teacherName}} anything", { teacherName })}
            </Typography>
          </Box>
        )}
    </>
  );
}

interface ThreadListViewProps {
  onConversationClick: (item: ConversationListItem) => void;
  onOpenGeneralThread: () => void;
  onClose: () => void;
}

export default function ThreadListView({
  onConversationClick,
  onOpenGeneralThread,
  onClose,
}: ThreadListViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadListView",
  );
  const {
    leader,
    section,
    teacherName,
    threads,
    feedbackThreads,
    threadsLoading,
    feedbackThreadsLoading,
    searchInput,
    debouncedSearch,
    setSearchInput,
  } = useMessageTeacherDrawerContext();
  const items = React.useMemo(() => {
    const merged: ConversationListItem[] = [
      ...threads.map(toConversationListItem),
      ...feedbackThreads,
    ];
    return sortConversationsByLatestSentAt(merged);
  }, [feedbackThreads, threads]);
  const showEmptyConversationsState =
    !threadsLoading &&
    !feedbackThreadsLoading &&
    items.length === 0 &&
    !debouncedSearch;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          p: 12,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <UserHeadshotImage user={leader} size={44} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
            {teacherName}
          </Typography>
          {Boolean(section.end_time) && (
            <Typography variant="caption" color="text.secondary">
              {t("Teacher access ends {{date}}", {
                date: dayjs(section.end_time).format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label={t("Close messages")}
          icon={faTimes}
          variant="text"
          onClick={onClose}
        />
      </Box>
      {!showEmptyConversationsState && (
        <Box
          sx={{
            py: 8,
            px: 12,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={t("Search all messages")}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon
                    icon={faSearch}
                    sx={{ color: "text.secondary", fontSize: "1rem" }}
                  />
                </InputAdornment>
              ),
              endAdornment:
                threadsLoading && debouncedSearch ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 20 },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <ThreadListBody
          items={items}
          threadsLoading={threadsLoading}
          feedbackThreadsLoading={feedbackThreadsLoading}
          showEmptyConversationsState={showEmptyConversationsState}
          teacherName={teacherName}
          debouncedSearch={debouncedSearch}
          onConversationClick={onConversationClick}
          onOpenGeneralThread={onOpenGeneralThread}
        />
      </Box>
    </>
  );
}
